import React, { useEffect, useMemo, useState } from 'react';
import { CircleAlert } from 'lucide-react';
import { useDispatch } from 'react-redux';
import BorderedCompanySelect from 'src/components/form-new/bordered-company-select';
import BorderedSkuSelect from 'src/components/form-new/bordered-sku-select';
import { portalDs } from 'src/components/portal-design-system';
import SkuHelperButton from 'src/components/sku-helper/sku-helper-button';
import { buildCampaignSkuContext } from 'src/components/sku-helper/sku-helper.types';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/components/ui/tooltip';
import { cn } from 'src/lib/utils';
import { Company } from 'src/pages/billing/types/billing.types';
import { useGetSKUsQuery } from 'src/redux/billing/billing.endpoints';
import notificationService from 'src/services/notification/notification.service';
import { useUpdateCampaignMutation } from '../endpoints/accounts.endpoints';
import { Campaign } from '../types/client-management.types';

interface CampaignTableProps {
  campaigns: Campaign[];
  companies: Company[];
  isRefreshing?: boolean;
  className?: string;
  onUpdateCampaign?: (campaignId: string, field: string, value: unknown) => void;
}

const CampaignTable: React.FC<CampaignTableProps> = ({
  isRefreshing,
  className,
  campaigns,
  companies,
  onUpdateCampaign,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [optimisticPatches, setOptimisticPatches] = useState<Record<string, Partial<Campaign>>>({});
  const dispatch = useDispatch();

  const [updateCampaign, { isLoading: isUpdating }] = useUpdateCampaignMutation();
  const { data: skus = [] } = useGetSKUsQuery({ includeArchived: true });

  const displayCampaigns = useMemo(
    () => campaigns.map((campaign) => ({ ...campaign, ...(optimisticPatches[campaign.id] ?? {}) })),
    [campaigns, optimisticPatches]
  );

  useEffect(() => {
    setOptimisticPatches((prev) => {
      if (Object.keys(prev).length === 0) {
        return prev;
      }
      const next = { ...prev };
      for (const [id, patch] of Object.entries(prev)) {
        const campaign = campaigns.find((c) => c.id === id);
        if (!campaign) {
          continue;
        }
        const matches = Object.entries(patch).every(
          ([key, value]) => campaign[key as keyof Campaign] === value
        );
        if (matches) {
          delete next[id];
        }
      }
      return next;
    });
  }, [campaigns]);

  useEffect(() => {
    setIsLoading(Boolean(isRefreshing || isUpdating));
  }, [isRefreshing, isUpdating]);

  const handleUpdateCampaign = async(campaignId: string, field: string, value: unknown) => {
    setOptimisticPatches((prev) => ({
      ...prev,
      [campaignId]: { ...(prev[campaignId] ?? {}), [field]: value },
    }));

    try {
      const updated = await updateCampaign({
        campaignId,
        [field]: value,
      }).unwrap();

      setOptimisticPatches((prev) => ({
        ...prev,
        [campaignId]: { ...(prev[campaignId] ?? {}), ...updated },
      }));
      onUpdateCampaign?.(campaignId, field, value);
      notificationService.handleSuccess('Campaign updated successfully', dispatch, 'Campaign updated successfully');
    } catch (error) {
      console.error('Failed to update campaign:', error);
      setOptimisticPatches((prev) => {
        const next = { ...prev };
        delete next[campaignId];
        return next;
      });
      notificationService.handleError(error, dispatch, 'Failed to update campaign');
    }
  };

  return (
    <div className={cn(portalDs.tableContainer, className)}>
      <Table className={cn('w-full table-fixed', isLoading && 'pointer-events-none opacity-50')}>
        <TableHeader className={portalDs.tableHead}>
          <TableRow className="border-0 hover:bg-transparent">
            <TableHead className={cn(portalDs.tableHeadCell, 'w-12')}>
              {' '}
            </TableHead>
            <TableHead className={cn(portalDs.tableHeadCell, 'w-[26%]')}>
              Title
            </TableHead>
            <TableHead className={cn(portalDs.tableHeadCell, 'w-[22%]')}>
              Account
            </TableHead>
            <TableHead className={cn(portalDs.tableHeadCell, 'w-[24%]')}>
              SKU
            </TableHead>
            <TableHead className={cn(portalDs.tableHeadCell, 'w-[24%]')}>
              Company
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {displayCampaigns.length === 0 ? (
            <TableRow className="hover:bg-transparent">
              <TableCell
                colSpan={5}
                className={cn(portalDs.tableCell, 'py-10 text-center', portalDs.tableCellMuted)}
              >
                No campaigns match the current filters.
              </TableCell>
            </TableRow>
          ) : (
            displayCampaigns.map((campaign: Campaign) => (
              <TableRow
                key={campaign.id}
                className={cn(portalDs.tableBodyRow, 'align-top')}
              >
                <TableCell className={portalDs.tableCell}>
                  {campaign.attention_needed ? (
                    <span
                      className="inline-flex text-[#DC2626]"
                      title="Attention needed"
                    >
                      <CircleAlert className="size-5 shrink-0" />
                    </span>
                  ) : null}
                </TableCell>
                <TableCell className={cn(portalDs.tableCell, 'font-medium')}>
                  <TooltipProvider delayDuration={300}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="block cursor-default truncate">
                          {campaign.title}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent
                        side="top"
                        className="max-w-sm"
                      >
                        {campaign.title}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </TableCell>
                <TableCell className={cn(portalDs.tableCell, portalDs.tableCellMuted)}>
                  <TooltipProvider delayDuration={300}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="block cursor-default truncate">
                          {campaign.account?.title ?? '—'}
                          {campaign.account?.title != null || campaign.account?.source != null ? ' | ' : ''}
                          {(campaign.account?.source ?? '')
                            .toString()
                            .toLowerCase()
                            .replace('_', ' ')}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent
                        side="top"
                        className="max-w-sm"
                      >
                        {campaign.account?.title ?? '—'}
                        {' | '}
                        {campaign.account?.source ?? ''}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </TableCell>
                <TableCell className={cn(portalDs.tableCell, 'py-2')}>
                  <div className="flex items-center gap-1">
                    <div className="min-w-0 flex-1">
                      <BorderedSkuSelect
                        skus={skus}
                        value={campaign.sku || ''}
                        onChange={(skuId) => handleUpdateCampaign(campaign.id, 'sku', skuId)}
                        label=""
                        campaignTitle={campaign.title}
                        attentionNeeded={campaign.attention_needed}
                        className="min-w-0 max-w-full text-xs"
                      />
                    </div>
                    <SkuHelperButton
                      context={buildCampaignSkuContext(campaign, {
                        companyName: companies.find((company) => company.id === campaign.company)?.company_name ?? null,
                        skus,
                      })}
                      skus={skus}
                      onApply={(skuId) => {
                        if (skuId) {
                          handleUpdateCampaign(campaign.id, 'sku', skuId);
                        }
                      }}
                    />
                  </div>
                </TableCell>
                <TableCell className={cn(portalDs.tableCell, 'py-2')}>
                  <BorderedCompanySelect
                    companies={companies}
                    value={campaign.company || ''}
                    onChange={(companyId) => handleUpdateCampaign(campaign.id, 'company', companyId)}
                    label=""
                    suggestionQuery={campaign.title}
                    campaignAccountCompanyId={campaign.account?.company || ''}
                    attentionNeeded={campaign.attention_needed}
                    className="min-w-0 max-w-full text-xs"
                  />
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default CampaignTable;
